import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { DEFAULT_EMPTY_VALUE, FilterDef, FilterValue } from './types';

/**
 * Padded, self-contained search box for the filter dropdowns. Uses cmdk's raw
 * input (so list filtering still works) but renders a bordered input with room
 * around it instead of the default edge-to-edge divider style.
 */
const FilterSearch: React.FC<{ placeholder: string }> = ({ placeholder }) => (
  <div className="p-2">
    <div className="flex items-center gap-2 rounded-md border border-input px-2.5">
      <Search className="h-4 w-4 shrink-0 opacity-50" />
      <CommandPrimitive.Input
        placeholder={placeholder}
        className="flex h-8 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
      />
    </div>
  </div>
);

interface FilterControlProps {
  def: FilterDef;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  /** Open the control immediately on mount (used when freshly added). */
  defaultOpen?: boolean;
}

const Trigger = React.forwardRef<
  HTMLButtonElement,
  { def: FilterDef; valueLabel?: string; active?: boolean } & React.ComponentPropsWithoutRef<typeof Button>
>(({ def, valueLabel, active, ...props }, ref) => (
  <Button
    ref={ref}
    variant="outline"
    className={cn(
      'h-9 gap-1.5 rounded-lg px-3 font-normal text-sm',
      active && 'border-primary/40 bg-primary/5',
    )}
    {...props}
  >
    {def.icon}
    <span className="text-foreground">
      {def.label}
      {valueLabel ? ':' : ''}
    </span>
    {valueLabel ? (
      <span className="max-w-[160px] truncate font-medium text-foreground">{valueLabel}</span>
    ) : null}
    <ChevronDown className="h-3.5 w-3.5 opacity-50" />
  </Button>
));
Trigger.displayName = 'FilterTrigger';

/**
 * A single self-triggering filter control rendered as a clean pill. Select and
 * multiselect use a Popover + Command list (optionally searchable); used both as
 * a pinned quick filter and inside active-filter chips.
 */
const FilterControl: React.FC<FilterControlProps> = ({ def, value, onChange, defaultOpen }) => {
  const [open, setOpen] = React.useState(Boolean(defaultOpen));

  if (def.type === 'select') {
    const empty = def.emptyValue ?? DEFAULT_EMPTY_VALUE;
    const active = !!value && value !== empty;
    const current = (def.options || []).find((o) => o.value === value);
    const valueLabel = active ? current?.label || String(value) : undefined;

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Trigger def={def} valueLabel={valueLabel} active={active} />
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="start">
          <Command>
            {def.searchable && <FilterSearch placeholder={`Search ${def.label.toLowerCase()}...`} />}
            <CommandList>
              <CommandEmpty>No results.</CommandEmpty>
              <CommandGroup>
                {(def.options || []).map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.label}
                    onSelect={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn('mr-2 h-4 w-4 shrink-0', value === o.value ? 'opacity-100' : 'opacity-0')}
                    />
                    {o.icon ? <span className="mr-2 flex items-center">{o.icon}</span> : null}
                    <span className="truncate">{o.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  if (def.type === 'multiselect') {
    const selected = Array.isArray(value) ? value : [];
    const active = selected.length > 0;

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Trigger def={def} valueLabel={active ? String(selected.length) : undefined} active={active} />
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="start">
          <Command>
            {def.searchable && <FilterSearch placeholder={`Search ${def.label.toLowerCase()}...`} />}
            <CommandList>
              <CommandEmpty>No results.</CommandEmpty>
              <CommandGroup>
                {(def.options || []).map((o) => {
                  const checked = selected.includes(o.value);
                  return (
                    <CommandItem
                      key={o.value}
                      value={o.label}
                      onSelect={() =>
                        onChange(checked ? selected.filter((v) => v !== o.value) : [...selected, o.value])
                      }
                    >
                      <Check className={cn('mr-2 h-4 w-4 shrink-0', checked ? 'opacity-100' : 'opacity-0')} />
                      <span className="truncate">{o.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  // text
  const active = !!value;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Trigger def={def} valueLabel={active ? String(value) : undefined} active={active} />
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-3" align="start">
        <Input
          autoFocus
          defaultValue={(value as string) || ''}
          placeholder={def.placeholder}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onChange((e.target as HTMLInputElement).value);
              setOpen(false);
            }
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default FilterControl;
